// Registro de módulos de la plataforma nueva. El shell arma el menú y las rutas desde aquí: un módulo nuevo
// o migrado sólo tiene que estar en esta lista. Las rutas son LAS MISMAS de la plataforma de siempre (App.js)
// para que ligas, correos y marcadores sigan funcionando al cambiar de plataforma.
//
// Campos:
//   clave           carpeta en src/nueva/modulos/<clave> (dueño: el agente/persona que lo migra)
//   titulo          etiqueta del menú (es-MX)
//   feature         llave de features de /api/user/config; sin feature = siempre visible
//   optIn           true = sólo con feature === true (módulos opcionales); si no, prendido salvo false
//   rutas           [{ ruta, Pagina, sinShell? }]; Pagina es lazy (cada módulo es su propio chunk)
//   NavSecundaria   (opcional, lazy) navegación propia del módulo que el shell muestra en el menú cuando
//                   el módulo está activo: conversaciones del chat, reportes de PowerBI, etc.
//   enMenu          false = tiene rutas pero no entrada propia en el menú (p. ej. reportes personalizados,
//                   que se abren desde la navegación del chat)
//   fallback        a dónde mandar si el usuario no tiene el módulo (default '/')
//   tituloI18n      llave de i18n del título (la de siempre traducía Medios, Alertas y Oportunidades); el enlace del
//                   menú muestra t(tituloI18n). En español es igual a `titulo`.
//   gesto           gesto del ícono del menú (AnimatedIcon: grow, ring, pop…)
//   ocultarSinReportes  (Analytics Dashboards) no aparece en el menú si el usuario no tiene reportes de PowerBI
//   externo         { usuarios, url }: para esos usuarios el enlace del menú abre la url en la misma pestaña
//   featureNav      feature que exige la NavSecundaria (default: la del módulo). Reportes, guía y geo muestran la
//                   del chat sólo a quien tiene AI Pricing
//
// El ORDEN es el del menú de la de siempre (sidebarmenu.jsx:976-1537).
import { lazy } from 'react';
import {
  Bell, BookOpen, FileCode2, FileText, Gauge, HelpCircle, House, LayoutDashboard, LineChart, Map, Newspaper, Settings,
  Store, TrendingUp, Wallet,
} from 'lucide-react';
import { DbAgent } from '../../ds/brand/DbIcons';

/** lazy() con precarga: el menú pide el chunk del módulo al pasar el puntero o enfocar su enlace, así al hacer
 *  clic ya está descargado. Si la descarga falla, el siguiente intento vuelve a pedirlo. */
function perezoso(cargar) {
  let promesa = null;
  const precargar = () => {
    if (!promesa) promesa = cargar().catch((e) => { promesa = null; throw e; });
    return promesa;
  };
  const Componente = lazy(precargar);
  Componente.precargar = precargar;
  return Componente;
}

// El chat se comparte: la MISMA página para /chat y /chat/:id (pasar de una a otra no remonta la página) y su
// navegación (conversaciones, consultas, reportes…) también en reportes, guía y geo, como el menú de siempre,
// donde la sección AI Pricing seguía visible en esas rutas (pedido del chat).
const PaginaChat = perezoso(() => import('./chat/Pagina'));
const PaginaMedios = perezoso(() => import('./medios/Pagina'));
const NavChat = perezoso(() => import('./chat/NavSecundaria'));

export const MODULOS = [
  {
    // Sólo en la plataforma nueva. Aparece si el portal le fijó un enfoque a la organización (bi_clients.enfoque →
    // features.inicio, backend/routes/inicio.py); al entrar por «/» se aterriza aquí (Shell.jsx).
    clave: 'inicio', titulo: 'Inicio', feature: 'inicio', optIn: true, icono: House, gesto: 'pop',
    rutas: [
      { ruta: '/inicio', Pagina: perezoso(() => import('./inicio/Pagina')) },
      { ruta: '/inicio/recorrido', Pagina: perezoso(() => import('./inicio/Recorrido')) },
    ],
  },
  {
    clave: 'chat', titulo: 'AI Pricing Engine', feature: 'chatAgent', icono: DbAgent, gesto: 'pop',
    rutas: [
      { ruta: '/chat', Pagina: PaginaChat },
      { ruta: '/chat/:id_conversation', Pagina: PaginaChat },
    ],
    NavSecundaria: NavChat,
  },
  {
    clave: 'dashboards', titulo: 'Analytics Dashboards', feature: 'powerbiReports', icono: LayoutDashboard,
    rutas: [{ ruta: '/reports', Pagina: perezoso(() => import('./dashboards/Pagina')) }],
    NavSecundaria: perezoso(() => import('./dashboards/NavSecundaria')),
    ocultarSinReportes: true, gesto: 'grow',
  },
  {
    // /medios es la del menú en la plataforma nueva: «/» es la puerta de entrada y quien tiene Inicio aterriza allá
    // (Shell.jsx). «/» sigue abriendo Medios (ligas y marcadores de siempre).
    clave: 'medios', titulo: 'Monitoreo de Medios', tituloI18n: 'menu.mediaMonitoring', feature: 'mediaMonitoring',
    icono: Newspaper, fallback: '/reports', gesto: 'pop',
    rutas: [
      { ruta: '/medios', Pagina: PaginaMedios },
      { ruta: '/', Pagina: PaginaMedios },
    ],
  },
  {
    clave: 'alertas', titulo: 'Alertas de Precios', tituloI18n: 'menu.priceAlerts', feature: 'priceAlerts', icono: Bell,
    gesto: 'ring',
    rutas: [{ ruta: '/alerts', Pagina: perezoso(() => import('./alertas/Pagina')) }],
  },
  {
    clave: 'oportunidades', titulo: 'Oportunidades Comerciales', tituloI18n: 'menu.opportunities', feature: 'opportunities',
    icono: TrendingUp, gesto: 'grow',
    rutas: [{ ruta: '/opportunities', Pagina: perezoso(() => import('./oportunidades/Pagina')) }],
  },
  {
    // Sin feature: en la de siempre /report/:id no tiene compuerta (App.js:621); un enlace viejo a un reporte abre.
    clave: 'reportes', titulo: 'Reportes personalizados', icono: FileCode2, enMenu: false,
    rutas: [
      { ruta: '/report/:id_report', Pagina: perezoso(() => import('./reportes/Pagina')) },
      { ruta: '/report/:id_report/print', Pagina: perezoso(() => import('./reportes/Impresion')), sinShell: true },
    ],
    NavSecundaria: NavChat, featureNav: 'chatAgent',
  },
  {
    clave: 'visuales', titulo: 'Visuales', feature: 'visuals', optIn: true, icono: LineChart, gesto: 'grow',
    rutas: [{ ruta: '/visuals', Pagina: perezoso(() => import('./visuales/Pagina')) }],
  },
  {
    clave: 'visualesDemo', titulo: 'Visuales demo', feature: 'visualsDemo', optIn: true, icono: Gauge, gesto: 'grow',
    // La de siempre manda a estos usuarios a un sitio externo en la misma pestaña (sidebarmenu.jsx:33-38).
    externo: {
      usuarios: (process.env.REACT_APP_VISUALS_EXTERNAL_USERS || 'admin-main-demo').split(',').map((u) => u.trim()).filter(Boolean),
      url: process.env.REACT_APP_VISUALS_EXTERNAL_URL || 'https://demo-web.data-bunker.ai/',
    },
    rutas: [{ ruta: '/visuals-demo', Pagina: perezoso(() => import('./visualesDemo/Pagina')) }],
  },
  {
    clave: 'insights', titulo: 'DataBunker Insights', feature: 'insights', optIn: true, icono: FileText,
    rutas: [{ ruta: '/insights', Pagina: perezoso(() => import('./insights/Pagina')) }],
  },
  {
    clave: 'knobloch', titulo: 'Knobloch', feature: 'knobloch', optIn: true, icono: Store,
    rutas: [{ ruta: '/knobloch', Pagina: perezoso(() => import('./knobloch/Pagina')) }],
  },
  {
    clave: 'lorealPricing', titulo: "Pricing L'Oréal", feature: 'lorealPricing', optIn: true, icono: Wallet,
    rutas: [{ ruta: '/loreal-pricing', Pagina: perezoso(() => import('./lorealPricing/Pagina')) }],
  },
  // «Visuales · <cliente>»: tableros de clientes de la maqueta (src/pages/VisualesClientes/tableros.js); un tablero por
  // opción del acordeón (?tablero=).
  {
    clave: 'visualesNike', titulo: 'Reports', feature: 'visualesNike', optIn: true, icono: LineChart, gesto: 'grow',
    rutas: [{ ruta: '/visuales-nike', Pagina: perezoso(() => import('./visualesClientes/Pagina').then((m) => ({ default: m.PaginaNike }))) }],
    NavSecundaria: perezoso(() => import('./visualesClientes/NavSecundaria').then((m) => ({ default: m.NavNike }))),
  },
  {
    clave: 'visualesTiendasBara', titulo: 'Visuales · Tiendas Bara', feature: 'visualesTiendasBara', optIn: true, icono: LineChart,
    gesto: 'grow',
    rutas: [{ ruta: '/visuales-tiendas-bara', Pagina: perezoso(() => import('./visualesClientes/Pagina').then((m) => ({ default: m.PaginaTiendasBara }))) }],
    NavSecundaria: perezoso(() => import('./visualesClientes/NavSecundaria').then((m) => ({ default: m.NavTiendasBara }))),
  },
  {
    clave: 'guia', titulo: 'Guía interactiva', icono: BookOpen, enMenu: false,
    rutas: [{ ruta: '/guia', Pagina: perezoso(() => import('./guia/Pagina')) }],
    NavSecundaria: NavChat, featureNav: 'chatAgent',
  },
  {
    clave: 'geo', titulo: 'Análisis geográfico', icono: Map, enMenu: false,
    rutas: [{ ruta: '/geo/:id_conversation', Pagina: perezoso(() => import('./geo/Pagina')) }],
    NavSecundaria: NavChat, featureNav: 'chatAgent',
  },
  {
    clave: 'admin', titulo: 'Administración', icono: Settings, enMenu: false,
    rutas: [{ ruta: '/admin', Pagina: perezoso(() => import('./admin/Pagina')) }],
  },
  {
    clave: 'cuenta', titulo: 'Cuenta y ayuda', icono: HelpCircle, enMenu: false,
    rutas: [
      { ruta: '/plan', Pagina: perezoso(() => import('./cuenta/Plan')) },
      { ruta: '/faq', Pagina: perezoso(() => import('./cuenta/Faq')) },
      { ruta: '/terms', Pagina: perezoso(() => import('./cuenta/Terminos')) },
      { ruta: '/contact', Pagina: perezoso(() => import('./cuenta/Contacto')) },
    ],
  },
];
